import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Bot, Check, Loader2, Minus, Star } from "lucide-react";
import { toast } from "sonner";
import React from "react";
import { useGetAiMarketplaceListingQuery, useAcquireAiAgentMutation } from "@/store/rest_api";
import { AI_AGENT_CATEGORIES } from "@/model/interface/AiAgent";
import { apiErrorMessage } from "@/util/apiError";
import {
  listingCostSentence,
  listingListPriceLabel,
  listingMonthlyLabel,
  listingPerMinuteLabel,
} from "@/util/aiListingPricing";

export default function AiMarketplaceListingPage() {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { data: listing, isLoading, isError } = useGetAiMarketplaceListingQuery(listingId!);
  const [acquireAgent, { isLoading: isAcquiring }] = useAcquireAiAgentMutation();
  const [isAcquireDialogOpen, setIsAcquireDialogOpen] = useState(false);

  const handleAcquire = async () => {
    if (!listing) {
      return;
    }
    try {
      await acquireAgent(listing.id).unwrap();
      toast.success("Agent acquired! You can now configure it.");
      setIsAcquireDialogOpen(false);
      navigate("/ai-agents");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to acquire agent"));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 max-w-4xl">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !listing) {
    return (
      <div className="space-y-6 p-4 max-w-4xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/ai-marketplace")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">Agent not available</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bot className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center max-w-md">
              This agent is no longer available in the marketplace.
            </p>
            <Button className="mt-4" variant="outline" onClick={() => navigate("/ai-marketplace")}>
              Back to marketplace
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const categoryLabel =
    AI_AGENT_CATEGORIES.find((cat) => cat.value === listing.template?.category)?.label || listing.template?.category;
  const isVideo = listing.media?.contentType?.startsWith("video");
  const perks = listing.perks ?? [];
  const limits = listing.limits ?? [];

  return (
    <div className="space-y-6 p-4 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/ai-marketplace")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3 flex-1">
          {listing.template?.iconUrl ? (
            <img src={listing.template.iconUrl} alt="" className="h-12 w-12 rounded-lg object-cover" />
          ) : (
            <div className="p-2.5 rounded-lg bg-cyan-500 text-white">
              <Bot className="h-7 w-7" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold tracking-tight">{listing.displayName || listing.template?.name}</h1>
              {listing.featured && <Star className="h-5 w-5 text-yellow-500 fill-yellow-500" />}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge variant="outline">{categoryLabel}</Badge>
              {listing.visibility === "PRIVATE" && <Badge>Private offer</Badge>}
            </div>
          </div>
        </div>
        <Button onClick={() => setIsAcquireDialogOpen(true)}>Acquire Agent</Button>
      </div>

      {listing.media?.url && (
        isVideo ? (
          <video src={listing.media.url} controls className="w-full max-h-96 rounded-lg border bg-black" />
        ) : (
          <img src={listing.media.url} alt="" className="w-full max-h-96 rounded-lg border object-cover" />
        )
      )}

      {listing.longDescription && (
        <p className="text-muted-foreground whitespace-pre-line">{listing.longDescription}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {perks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What it does</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {perks.map((perk, index) => (
                  <li key={`perk-${index}`} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-success" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {limits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">What it won't do</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {limits.map((limit, index) => (
                  <li key={`limit-${index}`} className="flex items-start gap-2 text-sm">
                    <Minus className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="text-muted-foreground">{limit}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-8 py-4">
          <div>
            <div className="text-sm text-muted-foreground">Monthly</div>
            <div className="text-lg font-semibold">{listingMonthlyLabel(listing)}</div>
            {listingListPriceLabel(listing) && (
              <div className="text-sm text-muted-foreground line-through">{listingListPriceLabel(listing)}</div>
            )}
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Per minute</div>
            <div className="text-lg font-semibold">{listingPerMinuteLabel(listing)}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Users</div>
            <div className="text-lg font-semibold">{listing.acquisitionCount}</div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={isAcquireDialogOpen} onOpenChange={setIsAcquireDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Acquire {listing.displayName || listing.template?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {listingCostSentence(listing)}
              {" "}You can configure and assign it to your call flows after acquiring.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcquire} disabled={isAcquiring}>
              {isAcquiring && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              Acquire
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
